import { useEffect, useState } from "react";
import DashSidebar from "@/components/DashSidebar";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Edit3, FileText } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const empty = { title: "", hook: "", body: "", category: "General", cover_image: "", published: true };

export default function AdminArticles() {
  const [articles, setArticles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const { data } = await api.get("/articles");
    setArticles(data);
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditing({}); };
  const openEdit = (a) => { setForm({ ...a }); setEditing(a); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing?.id) {
        await api.patch(`/articles/${editing.id}`, form);
        toast.success("Article updated");
      } else {
        await api.post("/articles", form);
        toast.success("Article created");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    await api.delete(`/articles/${id}`);
    load();
  };

  return (
    <div className="flex min-h-screen bg-[#F7F5F0]">
      <DashSidebar role="admin" />
      <main className="flex-1 lg:ml-0 ml-64 p-8 md:p-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#1A2E24]">Articles</h1>
            <p className="text-[#5C6B62] mt-1">{articles.length} articles · Easy to publish and edit.</p>
          </div>
          <Button onClick={openNew} className="bg-[#2C4C3B] hover:bg-[#1A2E24] text-white rounded-xl h-11" data-testid="new-article-btn">
            <Plus className="w-4 h-4 mr-2" /> New article
          </Button>
        </div>

        <div className="card-soft overflow-hidden">
          <div className="divide-y divide-[#E2DFD8]" data-testid="articles-list">
            {articles.map((a) => (
              <div key={a.id} className="p-5 flex items-center justify-between gap-4" data-testid={`article-row-${a.id}`}>
                <div className="flex items-start gap-4 flex-1 min-w-0">
                  <div className="w-12 h-12 rounded-xl bg-[#9CAF88]/15 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-[#2C4C3B]" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-[#9CAF88] uppercase tracking-wider mb-1">{a.category}</div>
                    <div className="font-display text-lg font-semibold text-[#1A2E24] truncate">{a.title}</div>
                    <div className="text-sm text-[#5C6B62] line-clamp-1">{a.hook}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className={`text-xs px-2 py-1 rounded-full ${a.published ? "bg-[#9CAF88]/20 text-[#2C4C3B]" : "bg-gray-200 text-gray-600"}`}>
                    {a.published ? "Published" : "Draft"}
                  </span>
                  <button onClick={() => openEdit(a)} className="text-[#2C4C3B] hover:text-[#1A2E24]" data-testid={`article-edit-${a.id}`}>
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button onClick={() => del(a.id)} className="text-[#E07A5F] hover:text-[#C96B52]" data-testid={`article-delete-${a.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
            {articles.length === 0 && <div className="p-8 text-center text-[#5C6B62]">No articles yet.</div>}
          </div>
        </div>

        <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">
                {editing?.id ? "Edit article" : "New article"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={save} className="space-y-4 mt-2">
              <div>
                <Label className="text-[#2C4C3B]">Title</Label>
                <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 h-11 rounded-xl bg-[#F7F5F0]" data-testid="article-title-input" />
              </div>
              <div>
                <Label className="text-[#2C4C3B]">Hook</Label>
                <Textarea required value={form.hook} onChange={(e) => setForm({ ...form, hook: e.target.value })} className="mt-1 rounded-xl bg-[#F7F5F0]" rows={2} data-testid="article-hook-input" />
              </div>
              <div>
                <Label className="text-[#2C4C3B]">Body <span className="text-xs text-[#5C6B62]">(use ## for section headings)</span></Label>
                <Textarea required value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="mt-1 rounded-xl bg-[#F7F5F0]" rows={12} data-testid="article-body-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[#2C4C3B]">Category</Label>
                  <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 h-11 rounded-xl bg-[#F7F5F0]" />
                </div>
                <div>
                  <Label className="text-[#2C4C3B]">Cover image URL</Label>
                  <Input value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} className="mt-1 h-11 rounded-xl bg-[#F7F5F0]" />
                </div>
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid="article-publish-switch" />
                <span className="text-sm text-[#2C4C3B]">Published</span>
              </label>
              <Button type="submit" className="w-full bg-[#2C4C3B] hover:bg-[#1A2E24] rounded-xl h-11" data-testid="article-save-btn">
                {editing?.id ? "Save changes" : "Create article"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
